import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronRight, Layers, Network, Compass } from 'lucide-react';
import SEO from '@/components/SEO';
import { useAuth } from '@/contexts/AuthContext';
import { PLAN_PRICES } from '@/config/pricing';
import { SAMPLE_REPORT_PUBLISHED, SAMPLE_REPORT_LINK_TEXT } from '@/pages/sampleReportContent';
import ScoreRing from '@/components/ds/ScoreRing';
import DimensionBars from '@/components/ds/DimensionBars';
import FindingRow from '@/components/ds/FindingRow';
import { SeverityBadge } from '@/components/shared/UiElements';
import AccordionItem from '@/components/ds/AccordionItem';
import PillTabs from '@/components/ds/PillTabs';
import ImagePlaceholder from '@/components/ds/ImagePlaceholder';
import FeaturePreview from '@/components/ds/FeaturePreview';
import { bandLabel } from '@/lib/scoreBands';

/* Severity, worst first. Declared here rather than inline so the chip row and
 * the step-3 chart cannot drift out of order or disagree about the words. */
const SEVERITY_ORDER = ['critical', 'major', 'minor'];
const SEVERITY_WORD = { critical: 'Critical', major: 'Major', minor: 'Minor' };
import { DEMO_AUDIT, FEATURES, STEPS, DIMENSIONS, AUDIENCES, FAQS,
         SOCIAL_PROOF, TRUST_STATS, HEADLINE_STATS, FREE_FAQ_ONE_A_MONTH, GUIDES } from '@/pages/homeLandingContent';
import HomeClaudeSection from '@/pages/HomeClaudeSection';
import PlanCards from '@/components/PlanCards';
import AuditCheckoutPopup from '@/components/AuditCheckoutPopup';
import { SEO_P1_LIVE } from '@/config/seoPreview';
import '@/styles/brand.css';
import '@/styles/forms.css';
import '@/styles/tabs.css';
import '@/styles/data.css';
import '@/styles/landing.css';

/**
 * The Landing v2 homepage.
 *
 * ============================================================================
 * BUILT FROM THE ARTBOARD, NOT FROM THE PAGE IT REPLACES
 * ============================================================================
 *
 * Source: docs/design-reference/uxauditpro-v1/UXAuditPro Landing v2.dc.html,
 * read in full. HomePageV2.js was the previous attempt and is NOT the basis for
 * this one: it reproduced the design's copy from an SEO inventory without its
 * visual structure, which is why `/` was rolled back to v1 on 2026-08-17. The
 * only thing taken from it is nothing; the SEO block below is lifted from the
 * LIVE HomePage.js, which is the page whose search presence must survive.
 *
 * ── NINE SECTIONS ───────────────────────────────────────────────────────────
 *
 *   1 hero              centred
 *   2 report card       left
 *   3 feature grid      centred heading, cards left
 *   4 how it works      left
 *   5 six dimensions    centred, the dark tonal panel
 *   6 audience          centred
 *   7 two products      centred      <- v1-only, see below
 *   8 pricing           centred
 *   9 FAQ               left
 *  10 closing CTA       centred
 *
 * "Two ways to build better products" (7) exists on live HomePage.js and has no
 * counterpart in the artboard. Content is preserved, so it is placed rather
 * than dropped: after the audience selector and before pricing, because
 * everything above it is the auditor alone, the audience section closes the
 * "who is this for" arc, and the price that follows is auditor-only. Ruled by
 * the owner 2026-08-19.
 *
 * ── WHAT THE ARTBOARD DRAWS THAT THIS DOES NOT ──────────────────────────────
 *
 * Five omissions, each because no legitimate content exists. Recorded in
 * docs/DECISIONS.md with what each would need before it could return:
 *
 *   1. The hero social-proof pill ("Used by 500+ product teams" + 3 avatars).
 *   2. The "Trusted by product teams" stat panel (500+ / 92% / 18 / <5min).
 *   3. The testimonial carousel. The artboard's OWN source marks all three
 *      quotes "PLACEHOLDER — DO NOT SHIP".
 *   4. The Anton stat pair (<5min, 92%) and the $3,000-$15,000 comparison.
 *   5. The Desktop/Mobile toggle over the dimension grid. We audit a desktop
 *      capture and a 390px mobile render, but mobile_ux is ONE dimension score,
 *      not a parallel set of six. A toggle over data with one state is a
 *      control that lies about what the product measures. It would need
 *      per-dimension scoring at both viewports — a pipeline change, not a UI
 *      one.
 *
 * ── THE H2 SIZE IS 38, NOT THE ARTBOARD'S 44 ────────────────────────────────
 *
 * Every section h2 in the artboard is 44px, and the closing h2 is 56px. 44 is
 * the PRE-PHASE-B maximum: `--type-h2-size` was reduced to
 * clamp(28px, 4vw, 38px) in Phase B, and type-scale.test.js:713 injects
 * `clamp(28px, 4vw, 44px)` by name as the likeliest single-character
 * regression. The artboard predates that decision. `.uxa-h2` wins — hardcoding
 * 44 here would reintroduce exactly what the guard exists to catch, and would
 * make this the only page in the system with larger headings than its
 * neighbours.
 */


/**
 * The cross-page analysis section (1c) is unconditional as of the 2026-09-01
 * cut-over. It shipped behind `/home-preview/` first; the owner reviewed the
 * rendered page and ruled ship, so the preview route, its prerender entry and
 * its robots Disallow were deleted in the same commit that flipped this on.
 *
 * A preview route left live is this page at a second URL, and a canonical is a
 * hint rather than a directive. The surface existed to be looked at once.
 */
export default function HomeLanding({ preview = false }) {
  // The P1 SEO copy: on /home-preview/ now, on / once SEO_P1_LIVE flips.
  const fresh = preview || SEO_P1_LIVE;
  const faqs = fresh ? FAQS.map((f) => (f.q === 'Is the first UX audit really free?' ? FREE_FAQ_ONE_A_MONTH : f)) : FAQS;
  const { login, user, setUser } = useAuth();
  const [audience, setAudience] = useState(0);
  const [step, setStep] = useState(0);
  const [faq, setFaq] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);


  /* The signed-in / signed-out fork is written out at each of the four CTA
   * sites rather than wrapped in a local <PrimaryCta> helper.
   *
   * THE WRAPPER WAS WRITTEN FIRST AND HID EVERY CONTROL IT RENDERED. The
   * fill-needs-a-ground guard resolves a control's ground by walking the
   * elements that ENCLOSE IT IN THE SOURCE TEXT. A control rendered through a
   * helper defined at the top of the file is, in the text, nowhere near the
   * dark card it appears inside — so the guard read it as sitting on the page
   * canvas and an injected 1.00:1 defect passed clean.
   *
   * Four repetitions is the price of every control being visible where it
   * actually renders. HomePage.js makes the same fork inline for the same
   * reason. */

  return (
    <div>
      <AuditCheckoutPopup
        visible={showCheckout}
        onClose={() => setShowCheckout(false)}
        user={user}
        setUser={setUser}
        source="home_page"
      />
      {/* SEO — reproduced from live HomePage.js. Every og: and twitter: tag the
          site emits is derived by components/SEO.js from these four props, so
          reproducing the props reproduces the tags. All three JSON-LD graphs
          are carried across byte for byte, including the deliberate ABSENCE of
          aggregateRating on SoftwareApplication.

          canonical stays "/" rather than the preview path: the preview is
          unlinked and absent from sitemap.xml, and if it is crawled anyway the
          canonical consolidates it onto the live homepage instead of competing
          with it. It also makes the cutover a route change and nothing else. */}
      <SEO
        fullTitle={fresh ? 'Free AI UX Audit Tool for Websites & Apps | UXAuditPro' : 'UXAuditPro, AI-Powered UX Audit Tool | Instant Reports'}
        description={fresh
          ? 'Start with a free AI UX audit every month. Full reports score 6 dimensions and rank what to fix first, all in under 5 minutes.'
          : 'Get an AI-powered UX audit in under 5 minutes. UXAuditPro scores your app across 6 dimensions, surfaces every friction point, and tells you exactly what to fix first. First audit free.'}
        noIndex={preview}
        canonical="/"
        ogTitle="UXAuditPro, AI UX Audit Tool | Instant Reports"
        /* TRAILING SLASH ON EVERY `url`, 2026-08-26. All three blocks said
           `https://www.uxauditpro.com` with no slash while every canonical on
           the site says `https://www.uxauditpro.com/`. That is a first-party
           machine-readable statement that the un-slashed form is the entity
           URL, and it is the most likely reason 19 un-slashed variants ended up
           in Search Console's "Page with redirect" bucket: nothing in the
           rendered HTML emits them, but this told Google they exist.

           THE SearchAction IS DELETED, not corrected. It advertised
           `/search?q={search_term_string}`, and there is no /search route --
           `location /` is try_files-or-404, so it returns a real 404. Google
           indexed the literal template URL anyway, brackets and all, and it has
           been sitting in the Indexed bucket picking up impressions since June.
           Markup for a feature that does not exist is not a markup bug to fix,
           it is a claim to withdraw. Do not restore this unless a real search
           page ships. */
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            url: 'https://www.uxauditpro.com/',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'UXAuditPro',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            url: 'https://www.uxauditpro.com/',
            description: 'AI-powered UX audit tool that scores apps across 6 dimensions and delivers prioritised fix roadmaps in under 5 minutes.',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
              description: fresh ? 'One free audit every month' : 'First audit free',
            },
            // No aggregateRating. It carried ratingValue 4.8 / reviewCount 47
            // and no such reviews exist. Fabricated review markup is a Google
            // structured-data policy violation (it can earn a manual action and
            // loss of rich results), not merely inaccurate copy. Do not restore
            // this block unless real, collectable review data backs it.
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'UXAuditPro',
            url: 'https://www.uxauditpro.com/',
            logo: 'https://www.uxauditpro.com/logo.png',
            sameAs: [
              'https://twitter.com/uxauditpro',
              'https://linkedin.com/company/uxauditpro',
            ],
          },
        ]}
      />

      {/* ═══ 1. HERO ═══════════════════════════════════════════════════════════
          Centred. The social-proof pill the artboard puts above the h1 is
          omission 1 — it claims 500+ product teams and draws three avatars for
          people who do not exist. */}
      <header className="uxa-pt-xl px-6" style={{ position: 'relative', textAlign: 'center' }}>
        <div className="uxa-hero-grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 1180, margin: '0 auto' }}>
          {/* OWNER-RULED CLAIM. See the ruling block in homeLandingContent.js:
              the count is not measured. Ruled 2026-08-19 and recorded there. */}
          <p className="uxa-proof-pill" style={{ margin: '0 0 28px' }}>
            <span className="uxa-proof-pill__faces" aria-hidden="true">
              {SOCIAL_PROOF.swatches.map((c) => (
                <span key={c} style={{ background: c }} />
              ))}
            </span>
            {SOCIAL_PROOF.label}
          </p>
          <h1 className="uxa-h1" style={{ color: 'var(--ink-1)', maxWidth: 900, margin: '0 auto' }}>
            AI-powered UX audits that{' '}<br />
            <span style={{ color: 'var(--accent-display)', fontStyle: 'italic', fontWeight: 700 }}>actually fix</span> your product
          </h1>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', maxWidth: 620, margin: '20px auto 0' }}>
            {fresh
              ? 'An AI UX audit tool that scores your app across 6 dimensions, surfaces every friction point, and tells you exactly what to fix first.'
              : 'AI-powered UX audits that score your app across 6 dimensions, surface every friction point, and tell you exactly what to fix first.'}
          </p>
          <div style={{ display: 'flex', gap: 22, justifyContent: 'center', alignItems: 'center', marginTop: 34, flexWrap: 'wrap' }}>
            {user ? (
              <Link to="/audit/new" className="uxa-submit uxa-submit--auto" data-testid="hero-cta-primary">
                Audit My App Free
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            ) : (
              <button onClick={login} className="uxa-submit uxa-submit--auto" data-testid="hero-cta-primary">
                Audit My App Free
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            )}
            <Link to="/ux-auditor/" className="uxa-submit uxa-submit--auto uxa-submit--secondary" data-testid="hero-cta-secondary">
              See Demo Audit
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
          <p style={{ margin: '16px auto 0', fontSize: 'var(--type-dense-size)', fontWeight: 500, color: 'var(--ink-2)' }}>
            {fresh ? 'No credit card · One free audit every month' : 'No credit card · First audit free'}
          </p>
        </div>
      </header>

      {/* ═══ 2. REPORT CARD ════════════════════════════════════════════════════
          Left-aligned, floating on the spectrum glow. Third permitted spectrum
          location; the glow is clipped and masked to its own stage so it can
          never reach the text above it.

          Every number below is the real audit DEMO_AUDIT transcribes. See the
          header of homeLandingContent.js for what the label does and does not
          claim. */}
      {/* Horizontal padding ONLY. `.uxa-glow-stage` owns padding-top: it is the
          rise the bloom lives inside, and brand.css marks it load-bearing —
          "the consumer sets no top margin of its own; this is the gap". The
          first build set `padding: '64px 24px 0'` here, which silently replaced
          the 72px rise with 64px of the consumer's own and moved the bloom
          relative to its mask. */}
      <section className="uxa-glow-stage" style={{ paddingLeft: 'var(--space-6)', paddingRight: 'var(--space-6)' }}>
        <div className="uxa-glow" aria-hidden="true">
          <span className="uxa-glow-bloom" />
          <span className="uxa-glow-grain" />
        </div>
        <div className="uxa-surface-float" style={{ maxWidth: 1180, margin: '0 auto', padding: 24 }}>
          <div className="uxa-report-head">
            <div>
              <div style={{ fontSize: 'var(--type-h3-size)', fontWeight: 700, margin: 0 }}>UX Audit Report</div>
              <p style={{ fontSize: 'var(--type-dense-size)', fontWeight: 500, color: 'var(--ink-2)', margin: '4px 0 0' }}>
                {DEMO_AUDIT.appName} · {DEMO_AUDIT.url} · {DEMO_AUDIT.completedAt}
              </p>
            </div>
            {/* Presentational chips, not controls: this is a picture of a
                report, and a button here would offer to download a PDF the
                visitor has not run. Rendered as spans so nothing is focusable
                that does nothing. */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }} aria-hidden="true">
              <span style={{ padding: '10px 18px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border-2)', fontSize: 'var(--type-dense-size)', fontWeight: 500 }}>Share</span>
              <span style={{ padding: '10px 18px', borderRadius: 'var(--radius-pill)', background: 'var(--ink-strong)', color: 'var(--text-inverse)', fontSize: 'var(--type-dense-size)', fontWeight: 500 }}>Download PDF</span>
            </div>
          </div>

          <div className="uxa-report-grid">
            {/* Left panel: score, band, severity counts */}
            <div className="uxa-report-panel">
              <div className="uxa-score-row">
                <ScoreRing score={DEMO_AUDIT.overall} size={120} animate={false} />
                <div>
                  <div style={{ fontSize: 'var(--type-h3-size)', fontWeight: 700, margin: 0 }}>Overall Score</div>
                  <p style={{ fontSize: 'var(--type-dense-size)', fontWeight: 500, color: 'var(--ink-2)', margin: '8px 0 0' }}>
                    {bandLabel(DEMO_AUDIT.overall)} · mean of assessed dimensions, capped by severity
                  </p>
                </div>
              </div>

              <hr className="uxa-report-rule" />

              <p className="uxa-report-label">Issues Found</p>
              {/* The severity chips carry the three-step meter and the word, so
                  severity is never colour alone.

                  ZERO ROWS ARE OMITTED, NOT RENDERED AS ZERO. The first build
                  drew "0 CRITICAL" in the full critical treatment — accent
                  fill, three-step meter, the word CRITICAL — which made the
                  loudest element in the panel a report that there is NO
                  problem, on the hero image of the homepage. Someone scanning
                  sees a critical badge and a number.

                  Ruled 2026-08-19: omit the row. "3 Major, 1 Minor" is
                  complete and true, and an absent row says zero more clearly
                  than a loud badge saying zero. The alternative considered was
                  rendering zero counts in the minor treatment regardless of
                  tier, which is quieter but says "minor" about criticals.

                  NOT a new severity tier. Inventing a zero state is the h3
                  mistake; this renders the tiers that have findings and stops. */}
              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                {SEVERITY_ORDER
                  .filter((sev) => DEMO_AUDIT.severityCounts[sev] > 0)
                  .map((sev) => (
                    <SeverityBadge
                      key={sev}
                      severity={sev}
                      label={`${DEMO_AUDIT.severityCounts[sev]} ${SEVERITY_WORD[sev]}`}
                    />
                  ))}
              </div>

              <hr className="uxa-report-rule" />

              {/* The artboard's "Annotated Screenshots" strip carried "3 of 12
                  annotations" and a "+9" chip over a hatched panel. Both counts
                  are invented. The panel stays, as the design's own
                  placeholder convention, and says what it is. */}
              <p className="uxa-report-label">Annotated Screenshots</p>
              <ImagePlaceholder
                caption="annotated capture — asset pending"
                height={112}
                radius="var(--radius-md)"
                style={{ marginTop: 12 }}
              />
            </div>

            {/* Right panel: the six dimensions and the findings */}
            <div className="uxa-report-panel">
              <div style={{ fontSize: 'var(--type-h3-size)', fontWeight: 700, margin: 0 }}>UX Dimensions</div>
              {/* Omission 5 was a Desktop/Mobile PillTabs here. */}
              <div style={{ marginTop: 18 }}>
                <DimensionBars scores={DEMO_AUDIT.scores} height={96} testId="report-dimensions" />
              </div>

              <div style={{ marginTop: 18 }}>
                <p className="uxa-report-label">Priority Findings</p>
                <div style={{ marginTop: 14 }}>
                  <FindingRow
                    severity={DEMO_AUDIT.featured.severity}
                    dimension={DEMO_AUDIT.featured.dimension}
                    title={DEMO_AUDIT.featured.title}
                    testId="report-finding-featured"
                  />
                </div>
              </div>

              {/* The locked strip. "3 more findings locked" is 4 total minus
                  the 1 a free viewer sees in full. There is no "including N
                  more Critical" line because this audit has no critical
                  findings; inventing one to fill the slot is the defect this
                  page exists to stop. */}
              <div className="uxa-dark-panel" style={{ marginTop: 14, padding: '16px 20px', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-inverse)', margin: 0 }}>
                    {DEMO_AUDIT.lockedCount} more findings locked
                  </p>
                  <p style={{ fontSize: 'var(--type-dense-size)', fontWeight: 500, color: 'var(--ink-4)', margin: '2px 0 0' }}>
                    Titles shown free, evidence and fixes in the full report
                  </p>
                </div>
                <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '6px 6px 6px 20px', borderRadius: 'var(--radius-pill)', background: 'var(--surface-white)', color: 'var(--ink-1)', fontSize: 'var(--type-body-size)', fontWeight: 500 }}>
                  {`Unlock $${PLAN_PRICES.STARTER.usd}`}
                  <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--ink-strong)', color: 'var(--text-inverse)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ArrowRight size={13} />
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* A QUIET SECOND DOOR, NOT A SECOND CTA. The card above is a picture
            of a report, so the reader looking at it is exactly the person who
            wants to see a real one. Tertiary skin deliberately: the primary
            action on this page stays "Audit My App Free", and a filled control
            here would compete with it. Held until the sample report's record
            is a real audit — see SAMPLE_REPORT_PUBLISHED. */}
        {SAMPLE_REPORT_PUBLISHED && (
          <p className="uxa-sample-link">
            <Link className="uxa-submit uxa-submit--auto uxa-submit--tertiary" to="/sample-report/">
              {SAMPLE_REPORT_LINK_TEXT}
            </Link>
          </p>
        )}
      </section>

      {/* ═══ TRUST PANEL ═══════════════════════════════════════════════════════
          Between the report card and the feature grid, per the artboard.

          EVERY FIGURE HERE IS OWNER-RULED, not measured. The reasoning, and
          what each would need to become true, is in homeLandingContent.js
          beside the values. `<5min` is the exception: it is already live copy. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-dark-panel" style={{ padding: '56px 48px' }}>
          {/* Was a <p> at 24px/500, which left this section with no heading at
              all while a paragraph did a heading's job. It is the section's
              title, so it takes the real tag and the h2 role - 24px -> 38px. */}
          <h2 className="uxa-h2" style={{ margin: 0, color: 'var(--text-inverse)', textAlign: 'center' }}>
            {TRUST_STATS.heading}
          </h2>
          <div className="uxa-trust-grid">
            {TRUST_STATS.tiles.map((t) => (
              <div key={t.label}>
                <div className="uxa-trust-value">{t.value}</div>
                <p className="uxa-trust-label">{t.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 3. FEATURE GRID ═══════════════════════════════════════════════════
          Centred heading, cards left. Copy verbatim from live HomePage.js.
          Omitted between 2 and 3: the artboard's "Trusted by product teams"
          panel with four stat counters. Omission 2. */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
        <span className="section-label">What You Get</span>
        <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>
          Not just a score.{' '}<br />A complete diagnosis.
        </h2>
        <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>
          Every audit comes with actionable insights across 6 UX dimensions, prioritized by impact and effort.
        </p>
        <div className="uxa-grid-3" style={{ marginTop: 56 }}>
          {FEATURES.map((f) => (
            <div key={f.title} className="uxa-feature-card">
              <div className="uxa-feature-card__bloom" aria-hidden="true" />
              <FeaturePreview kind={f.preview} />
              <h3 className="uxa-h3" style={{ margin: '24px 0 8px' }}>{f.title}</h3>
              <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ 4. HOW IT WORKS ═══════════════════════════════════════════════════
          Left. Sticky rail, three steps. All three previews are rendered at
          once; the rail marks which one the reader is on rather than hiding the
          other two, so nothing is behind a control that a crawler cannot
          operate and nothing is lost with JS off. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-split">
          <div className="uxa-split__rail">
            <span className="section-label">How It Works</span>
            {/* NO HARD BREAK. The <br /> here forced "in under 5 minutes"
                onto its own line, and in the sticky rail's narrow column the
                first half ALSO wrapped - after "actionable" - which left
                "report" alone on a line of its own. A manual break assumes a
                column width; this one is a rail that changes width at every
                breakpoint. `balance` lets the browser pick even lines at
                whatever width it actually gets, and falls back to ordinary
                wrapping where it is unsupported. */}
            <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px 0 0', textWrap: 'balance' }}>
              From URL to actionable report in under 5 minutes
            </h2>
            <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px 0 0' }}>
              Our automated UX audit software analyses your app against 50+ research-backed heuristics from{' '}
              <a href="https://www.nngroup.com/articles/ten-usability-heuristics/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)', textDecoration: 'underline' }}>Nielsen Norman Group</a>{' '}
              and{' '}
              <a href="https://baymard.com/research" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)', textDecoration: 'underline' }}>Baymard Institute</a>{' '}
              — the gold standard in UX and e-commerce research.
            </p>
            <nav className="uxa-step-nav" aria-label="Audit steps">
              {STEPS.map((s, i) => (
                <button
                  key={s.n}
                  type="button"
                  className="uxa-step-nav__item"
                  aria-current={step === i}
                  onClick={() => setStep(i)}
                >
                  {s.n} · {s.label}
                </button>
              ))}
            </nav>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {STEPS.map((s, i) => (
              <div key={s.n} className="uxa-feature-card" style={{ background: 'var(--surface-panel)', overflow: 'visible' }}>
                <div className="uxa-feature-card__frame">
                  {i === 0 && (
                    <div aria-hidden="true">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', border: '1px solid var(--border-2)', borderRadius: 'var(--radius-pill)', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--ink-2)' }}>https://</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14 }}>{DEMO_AUDIT.url}</span>
                        <span style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 'var(--radius-pill)', background: 'var(--accent)', color: 'var(--ink-1)', fontSize: 'var(--type-dense-size)', fontWeight: 500 }}>Scan</span>
                      </div>
                      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                        <ImagePlaceholder caption="desktop capture" height={76} radius="var(--radius-field)" style={{ flex: 1 }} />
                        <ImagePlaceholder height={76} radius="var(--radius-field)" style={{ width: 46, flex: 'none' }} />
                      </div>
                    </div>
                  )}
                  {i === 1 && (
                    /* The artboard drew a dashed "Your previous audit" baseline
                       across this chart. Nothing in this product retains a
                       previous audit's scores, so the baseline is removed. The
                       six bars are the real audit and the callout lands on
                       Accessibility 45, which is genuinely its weakest. */
                    <div>
                      <p style={{ fontSize: 'var(--type-dense-size)', fontWeight: 700, color: 'var(--ink-2)', margin: '0 0 14px' }}>Dimension scores</p>
                      <DimensionBars scores={DEMO_AUDIT.scores} height={110} testId="step-dimensions" />
                      <p style={{ fontSize: 'var(--type-dense-size)', fontWeight: 700, color: 'var(--ink-2)', margin: '12px 0 0' }}>
                        Weakest area: Accessibility, {DEMO_AUDIT.scores.accessibility}
                      </p>
                    </div>
                  )}
                  {i === 2 && (
                    <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {/* Same rule as the chips above: a tier with no findings
                          gets no row, rather than a labelled zero-length bar. */}
                      {SEVERITY_ORDER
                        .filter((sev) => DEMO_AUDIT.severityCounts[sev] > 0)
                        .map((sev) => [sev, DEMO_AUDIT.severityCounts[sev]])
                        .map(([sev, n]) => (
                        <span key={sev} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ width: 96, flex: 'none' }}><SeverityBadge severity={sev} /></span>
                          <span style={{ flex: 1, height: 14, borderRadius: 7, background: 'var(--track-empty)' }}>
                            <span style={{ display: 'block', width: `${(n / 4) * 100}%`, height: '100%', borderRadius: 7, background: sev === 'minor' ? 'var(--border-2)' : sev === 'major' ? 'var(--accent-soft)' : 'var(--accent)' }} />
                          </span>
                          <span style={{ fontSize: 'var(--type-dense-size)', fontWeight: 500, color: 'var(--ink-2)', width: 12 }}>{n}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <h3 className="uxa-h3" style={{ margin: '24px 0 8px' }}>{s.title}</h3>
                <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 5. THE SIX DIMENSIONS ═════════════════════════════════════════════
          The page's tonal break: a dark #282628 panel at radius 34 with the
          72px white grid at 0.06. Centred heading, white. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-dark-panel">
          <div style={{ textAlign: 'center' }}>
            <span className="uxa-dark-panel__eyebrow">The 6 Dimensions</span>
            <h2 className="uxa-h2" style={{ color: 'var(--text-inverse)', margin: '20px auto 0', maxWidth: 760 }}>
              Every dimension that determines{' '}<br />whether users succeed or quit
            </h2>
            <p className="uxa-lead" style={{ color: 'var(--ink-4)', margin: '16px auto 0', maxWidth: 620 }}>
              Our UX audit tool scores your product across the six areas that{' '}
              <a href="https://www.nngroup.com/articles/user-experience-definition/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-soft)', textDecoration: 'underline' }}>UX research consistently identifies</a>{' '}
              as the root causes of user drop-off, low conversion, and poor retention.
            </p>
          </div>
          <div className="uxa-dim-grid">
            {DIMENSIONS.map((d) => (
              <div key={d.title} className="uxa-dim-grid__cell">
                <h3 className="uxa-h3" style={{ color: 'var(--text-inverse)', margin: '0 0 8px' }}>{d.title}</h3>
                <p className="uxa-lead" style={{ color: 'var(--ink-4)', margin: 0 }}>{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 6. AUDIENCE SELECTOR ══════════════════════════════════════════════
          Centred. PillTabs from Phase F: three tones, real tablist semantics,
          arrow-key roving, and the shared border cue so tone-solid is never
          carried by colour alone. */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
        <span className="section-label">Who It&apos;s For</span>
        <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0' }}>
          Built for every team that ships product
        </h2>
        <div style={{ marginTop: 32, display: 'flex', justifyContent: 'center' }}>
          <PillTabs
            items={AUDIENCES.map((a) => a.tab)}
            value={audience}
            onChange={setAudience}
            tone="solid"
            size="lg"
            ariaLabel="Choose an audience"
          />
        </div>
        {/* ALL THREE PANELS ARE IN THE DOM. Only one is visible, but the other
            two are `hidden` rather than absent.

            THE FIRST BUILD RENDERED ONLY THE SELECTED ONE and the SEO diff came
            back with a non-empty deletions list: two h3 headings and two
            internal links with their anchor text vanished from the page,
            because live HomePage.js draws all three audience cards at once and
            a tablist draws one. That is the design implying less content than
            the page has, and the rule is that the design loses. Keeping every
            panel mounted costs nothing, is what the ARIA tabs pattern specifies
            anyway, and puts the deletions list back to empty. */}
        {AUDIENCES.map((a, i) => (
          <div
            key={a.tab}
            role="tabpanel"
            hidden={audience !== i}
            aria-label={a.tab}
            className="uxa-price-card"
            style={{
              marginTop: 32,
              textAlign: 'left',
              backgroundImage: 'linear-gradient(var(--border-2) 1px, transparent 1px), linear-gradient(90deg, var(--border-2) 1px, transparent 1px)',
              backgroundSize: '72px 72px',
            }}
          >
            <div style={{ maxWidth: 720 }}>
              <h3 className="uxa-h3" style={{ margin: '0 0 12px' }}>{a.title}</h3>
              <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{a.body}</p>
              <Link to={a.link} className="uxa-submit uxa-submit--auto uxa-submit--tertiary" style={{ marginTop: 24 }}>
                {a.linkText}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        ))}
      </section>

      {/* ═══ 7. TWO PRODUCTS ═══════════════════════════════════════════════════
          v1-only. Not in the artboard; carried from live HomePage.js under
          content preservation and placed here by the owner's ruling — after the
          audience arc closes, before an auditor-only price. Copy verbatim. */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
        <span className="section-label">Our Products</span>
        <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0' }}>
          Two ways to build better products
        </h2>
        <div className="uxa-grid-2" style={{ marginTop: 56 }}>
          <div className="uxa-feature-card" style={{ background: 'var(--surface-white)' }}>
            <span style={{ display: 'inline-block', padding: '4px 12px', marginBottom: 16, fontSize: 'var(--type-dense-size)', fontWeight: 700, borderRadius: 'var(--radius-pill)', background: 'var(--accent-text)', color: 'var(--on-accent)' }}>LIVE</span>
            <h3 className="uxa-h3" style={{ margin: '0 0 12px' }}>AI UX Auditor</h3>
            <p style={{ fontSize: 'var(--type-body-size)', lineHeight: 'var(--type-body-leading)', color: 'var(--ink-2)', margin: '0 0 24px' }}>
              Upload screenshots of your app and get a comprehensive UX audit in minutes. Scored across 6 dimensions with prioritized fixes.
            </p>
            <ul className="uxa-price-list" style={{ fontSize: 14, marginBottom: 24 }}>
              {['6 UX dimension scores', 'Prioritized findings', 'PDF export', 'Sprint roadmap'].map((i) => (
                <li key={i}><Check className="w-4 h-4" style={{ color: 'var(--success)', flex: 'none', marginTop: 3 }} aria-hidden="true" />{i}</li>
              ))}
            </ul>
            <Link to="/ux-auditor/" className="uxa-submit uxa-submit--auto uxa-submit--tertiary">
              Learn more
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="uxa-feature-card" style={{ background: 'var(--surface-white)' }}>
            <span style={{ display: 'inline-block', padding: '4px 12px', marginBottom: 16, fontSize: 'var(--type-dense-size)', fontWeight: 700, borderRadius: 'var(--radius-pill)', background: 'var(--surface-sand-deep)', color: 'var(--ink-2)', border: '1px solid var(--border-2)' }}>COMING SOON</span>
            <h3 className="uxa-h3" style={{ margin: '0 0 12px' }}>AI MVP Validator</h3>
            <p style={{ fontSize: 'var(--type-body-size)', lineHeight: 'var(--type-body-leading)', color: 'var(--ink-2)', margin: '0 0 24px' }}>
              Know if your startup idea will work before you build it. Get market validation with AI research and human expertise.
            </p>
            <ul className="uxa-price-list" style={{ fontSize: 14, marginBottom: 24 }}>
              {['Market sizing (TAM/SAM/SOM)', 'Competitor analysis', 'Customer evidence', 'GO/PIVOT/NO-GO verdict'].map((i) => (
                <li key={i}><Check className="w-4 h-4" style={{ color: 'var(--ink-2)', flex: 'none', marginTop: 3 }} aria-hidden="true" />{i}</li>
              ))}
            </ul>
            <Link to="/mvp-validator/" className="uxa-submit uxa-submit--auto uxa-submit--tertiary">
              Join waitlist
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ 7a. WORKS INSIDE CLAUDE ═══════════════════════════════════════════
          Owner, 2026-10-08: directly after the two products, as the Claude
          Design handoff draws it (HomeClaudeSection.jsx says what tokens.css
          overruled). Preview-gated with the rest of the P1 copy. */}
      {fresh && <HomeClaudeSection />}

      {/* ═══ HEADLINE STATS ════════════════════════════════════════════════════
          Immediately before pricing, per the artboard (which places it after
          the testimonial carousel — still omitted, its own source marks all
          three quotes PLACEHOLDER — DO NOT SHIP).

          OWNER-RULED, as above. The 92% has no instrument behind it at all. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-headline-stats">
          <div className="uxa-headline-stats__edge" aria-hidden="true" />
          <div className="uxa-headline-stats__grid">
            {HEADLINE_STATS.map((h) => (
              <div key={h.value}>
                <div className="uxa-headline-stats__value">{h.value}</div>
                <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '20px 0 0', maxWidth: 380 }}>
                  {h.body}
                </p>
                {h.bars ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24, maxWidth: 380 }}>
                    {h.bars.map((bar) => (
                      <span key={bar.label} className="uxa-headline-stats__bar">
                        <span style={{ fontWeight: bar.strong ? 700 : 500, color: bar.strong ? 'var(--ink-1)' : 'var(--ink-2)' }}>
                          {bar.label}
                        </span>
                        <span className="uxa-headline-stats__track" aria-hidden="true">
                          <span style={{ width: `${bar.pct}%`, background: bar.strong ? 'var(--accent)' : 'var(--accent-soft)' }} />
                        </span>
                      </span>
                    ))}
                  </div>
                ) : null}
                {h.note ? (
                  <p style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 24, fontSize: 'var(--type-dense-size)', fontWeight: 500, color: 'var(--ink-2)' }}>
                    <span aria-hidden="true" style={{ display: 'flex' }}>
                      {[0, 1, 2].map((i) => (
                        <span key={i} style={{ width: 34, height: 34, borderRadius: '50%', border: '2px solid var(--surface-panel)', marginLeft: i ? -10 : 0, background: 'repeating-linear-gradient(135deg, var(--surface-sand-deep) 0 6px, var(--track-empty) 6px 12px)' }} />
                      ))}
                    </span>
                    {h.note}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 7b. THREE PAGES ═══════════════════════════════════════════════════
          NEW SECTION, 2026-08-26. Numbered 7b rather than 8 so that renumbering
          does not become the diff -- every section below keeps the number it
          has had, and the SEO diff stays readable.

          THE REASON IT EXISTS: this page was selling the product as it was
          before multi-page shipped. A paid audit covers three pages of one site
          and runs a comparison across them; neither fact appeared anywhere on
          this page, in the cards, the FAQ or the structured data. The single
          largest reason to pay was invisible at the point of sale.

          Placed immediately BEFORE pricing on purpose. The cards now lead with
          "1 page" against "Up to 3 pages of the same site", and a reader meets
          that difference better having just been told what the third pass does
          than having to work it out from a list item.

          Additive, not a rewrite. No section above or below moved, no heading
          changed level or wording, and nothing was reworded to make room. The
          same three cards run on /ux-auditor/, where the fuller trace to code
          sits beside them. */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl" data-testid="multi-page-section">
        <span className="section-label">Multi-Page</span>
        <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>
          Three pages, and what runs between them
        </h2>
        <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>
          A paid audit covers up to three pages of the same site. Each is scored on its own, and a
          third pass looks only at what is true between them.
        </p>
        <div className="uxa-grid-3" style={{ marginTop: 56 }}>
          {[
            {
              icon: Layers,
              title: 'Every page scored on its own',
              desc: 'Pages two and three go through the same six-dimension analysis as the first, each with its own score, its own findings and its own evidence. There is no site-wide average, deliberately: an average of three pages describes none of them, so every number stays attached to the page it came from.',
            },
            {
              icon: Network,
              title: 'Findings that only exist across pages',
              desc: 'The comparison pass reports relationships rather than repeats. What differs between your pages, which page is the outlier in a dimension, what holds consistently across all of them, and what degrades along a path, the way form fields can climb from one step to the next. A claim that names only one page is discarded before you see it.',
            },
            {
              icon: Compass,
              title: 'What it will not do',
              desc: 'Every page has to belong to the same site: a conclusion about "the site" drawn across two unrelated domains is not so much wrong as meaningless. Multi-page needs URLs, so a screenshot upload is a one-page audit. And if a page fails to capture, you lose that page\u2019s section and the report says so, never the whole audit.',
            },
          ].map((f) => (
            <div key={f.title} className="uxa-feature-card">
              <div className="uxa-feature-card__bloom" aria-hidden="true" />
              <span style={{ width: 52, height: 52, borderRadius: 'var(--radius-md)', background: 'var(--accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <f.icon className="w-6 h-6" style={{ color: 'var(--accent-text)' }} aria-hidden="true" />
              </span>
              <h3 className="uxa-h3" style={{ margin: '24px 0 8px' }}>{f.title}</h3>
              <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>


      {/* ═══ 8. PRICING ════════════════════════════════════════════════════════
          Centred. NEW to the homepage — live HomePage.js has no pricing
          section, so nothing is being replaced here.

          Every figure is read off backend/pricing.py, not the artboard:
          FREE usd 0 with lifetime_audits 1, STARTER usd 49, and the
          white_label add-on at usd 25. test_pricing_copy_matches_backend.py
          pins them, so a price change fails the build instead of leaving a
          stale number on the homepage.

          The artboard's "Most Popular" badge is dropped: a popularity claim
          with no data behind it. The artboard's "Every verified finding
          (avg 18)" is dropped from this card for the same reason the number is
          not propagated anywhere new.

          ── BOTH LISTS REWRITTEN 2026-08-26, TO LEAD WITH THE DIFFERENCE ──

          They led with deliverables, and the deliverables are nearly the same
          on both sides of the wall: a report, findings, a score. What actually
          separates the tiers is that one covers a page and the other covers a
          site, and that appeared nowhere on this page. "1 page" against "Up to
          3 pages of the same site" is legible on sight; "Every verified
          finding" against "Overall score out of 100" required the reader to
          infer the difference.

          Cross-page analysis is second on the paid list because it is the one
          thing a single-page audit tool cannot produce at any price. Every
          line is traced to code; the trace is written out beside the matching
          list in UxAuditorPage.js rather than duplicated here, because the two
          cards now say the same things and one copy of the evidence is enough.

          PER AUDIT, NOT ONE-TIME. "One-time" answers a question nobody asked
          ("will this recur?"); "per audit" answers the one they did ("what do
          I get for $49?") and makes the compare feature honest -- a second run
          is a second audit, not a second charge for the same one. The backend
          field is named pay_per_audit.

          "YOUR MOST SEVERE FINDING IN FULL" IS GONE, and this is the only line
          on this page removed rather than added. It was not what the code does.
          report_format.order_findings_for_free_tier severity-sorts, then
          promotes index 0 to the highest-severity finding that is desktop AND
          carries a measured value: a mobile finding can never take the slot,
          and a measured lower-severity finding outranks an unmeasured
          higher-severity one. The rule is correct -- the free tier's job is to
          prove we measured the site, and a measured 1.35:1 contrast failure
          proves that better than a vaguer, worse-sounding note. So the copy
          moved and the code did not. "One finding in full" drops the ordering
          claim and keeps the promise.

          "No credit card required" and "White-label branding +$25" are kept,
          appended rather than dropped: the first is true and does real work at
          the top of the funnel, and the second is the only place this page
          sells a $25 add-on that exists. */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
        <span className="section-label">Pricing</span>
        <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0' }}>Simple, transparent pricing</h2>
        <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>
          {fresh ? 'You get one free audit every month.' : 'Your first audit is free.'} Unlock the complete report when you&apos;re ready to fix everything.
        </p>
        <div className="uxa-grid-3" style={{ marginTop: 56, alignItems: 'start' }}>
          <PlanCards onCheckout={() => setShowCheckout(true)} />
        </div>
      </section>

      {/* ═══ 9. FAQ ════════════════════════════════════════════════════════════
          Left. AccordionItem from the DS. Copy verbatim from live HomePage.js.

          The live page renders these as static h3 + p, not an accordion. The
          accordion is the artboard's presentation and the questions keep their
          heading level, so this is a presentation change and not a content one:
          every question and every answer is present in the DOM either way. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-split">
          <div>
            <span className="section-label">FAQ</span>
            <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px 0 0' }}>
              Common questions about{' '}<br />UX audits
            </h2>
            <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px 0 0' }}>
              Discover the solutions to all your inquiries right here.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                question={<h3 className="uxa-disclosure-title" style={{ margin: 0 }}>{f.q}</h3>}
                open={faq === i}
                onToggle={() => setFaq(faq === i ? -1 : i)}
              >
                <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{f.a}</p>
              </AccordionItem>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 10. CLOSING CTA ═══════════════════════════════════════════════════
          Centred. The artboard sets this h2 at 56px; `.uxa-h2` is the system
          scale and wins, for the same reason the section h2s are 38 and not 44.

          Omitted before this band: the artboard's testimonial carousel
          (omission 3) and its Anton stat pair (omission 4). */}
      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>Ready to fix your UX?</h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '18px auto 0', maxWidth: 520 }}>
            {fresh ? 'One free audit every month. No credit card required.' : 'Get your first audit completely free. No credit card required.'}
          </p>
          <div style={{ position: 'relative', display: 'inline-block', marginTop: 40, isolation: 'isolate' }}>
            <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
              {user ? (
                <Link to="/audit/new" className="uxa-submit uxa-submit--auto" data-testid="cta-primary">
                  Audit My App Free
                  <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                </Link>
              ) : (
                <button onClick={login} className="uxa-submit uxa-submit--auto" data-testid="cta-primary">
                  Audit My App Free
                  <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                </button>
              )}
            </span>
          </div>
        </div>
      </section>

      {/* ═══ 11. GUIDES (SEO brief, Task 5) ═══════════════════════════════════
          Six links into the UX audit guides, directly above the footer.
          Preview-gated with the rest of the P1 copy: live / is unchanged
          until SEO_P1_LIVE. */}
      {fresh && (
        <section className="uxa-landing-section uxa-pt-xl" style={{ paddingBottom: 'var(--space-24)' }} data-testid="home-guides" aria-labelledby="home-guides-title">
          <h2 id="home-guides-title" className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>Guides</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '4px 32px' }}>
            {GUIDES.map((g) => (
              <li key={g.href}>
                <Link to={g.href} style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, color: 'var(--accent-text)', textDecoration: 'underline' }}>
                  {g.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
